import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Space, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import type { SetupStep } from '@velora/contracts';
import { ApiError, productApi } from '../../api';

/**
 * Shared footer for every Setup step page — a "Mark this step done" button that
 * hits POST /setup/progress. If the step is already done, shows the checkmark
 * pill and offers to update the summary. Invalidates the institution query so
 * the checklist ticks green on the way back.
 */
export function StepFooter({
  institution,
  step,
  summary,
  disabled,
  disabledReason,
  nextHref,
  nextLabel,
}: {
  institution: string;
  step: SetupStep;
  summary?: string;
  disabled?: boolean;
  disabledReason?: string;
  nextHref?: string;
  nextLabel?: string;
}) {
  const qc = useQueryClient();
  const progress = useQuery({
    queryKey: ['setup-progress', institution],
    queryFn: () => productApi.getSetupProgress(institution),
    enabled: !!institution,
  });
  const done = progress.data?.entries.some((e) => e.step === step) ?? false;

  const mark = useMutation({
    mutationFn: () => productApi.markStepDone(institution, { step, summary: summary ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['setup-progress', institution] });
      void qc.invalidateQueries({ queryKey: ['institution', institution] });
    },
  });

  const errBody = mark.error instanceof ApiError ? (mark.error.body as { message?: string } | null) : null;
  const errMsg = errBody?.message ?? (mark.error instanceof Error ? mark.error.message : null);

  return (
    <div
      style={{
        marginTop: 20,
        padding: '14px 0 4px',
        borderTop: '1px solid #eeeef1',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      {done ? (
        <Space size={8}>
          <CheckCircleFilled style={{ color: '#1c6442', fontSize: 18 }} />
          <Typography.Text style={{ color: '#1c6442', fontWeight: 500 }}>Marked done</Typography.Text>
          <Button size="small" onClick={() => mark.mutate()} loading={mark.isPending}>
            Update summary
          </Button>
        </Space>
      ) : (
        <Button
          type="primary"
          onClick={() => mark.mutate()}
          disabled={disabled}
          loading={mark.isPending}
          title={disabled ? disabledReason : undefined}
        >
          Mark this step done
        </Button>
      )}
      {nextHref && (
        <Button href={nextHref} type={done ? 'primary' : 'default'}>
          {nextLabel ?? 'Next step →'}
        </Button>
      )}
      {errMsg && <Alert type="error" showIcon message={errMsg} style={{ padding: '4px 10px' }} />}
      {disabled && !done && disabledReason && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {disabledReason}
        </Typography.Text>
      )}
    </div>
  );
}
